import React from 'react';

/**
 * Error Boundary component that catches crashes in the Agentic AI component
 * and provides user-friendly error recovery options.
 * 
 * Usage: Wrap any component that might crash
 * <AgentErrorBoundary>
 *   <AgenticAIComponent />
 * </AgentErrorBoundary>
 */
class AgentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      errorDetails: null,
      errorInfo: null,
      retryCount: 0 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('🚨 Agent Error Caught by Boundary:', error);
    console.error('Component Stack:', errorInfo.componentStack);
    
    this.setState({
      errorDetails: error,
      errorInfo: errorInfo
    });
    
    // Optional: Log to monitoring service (Application Insights, Sentry, etc.)
    if (window.appInsights) {
      window.appInsights.trackException({
        error: error,
        severityLevel: 3, // Error
        properties: {
          componentStack: errorInfo.componentStack,
          retryCount: this.state.retryCount
        }
      });
    }
  }

  handleRetry = () => {
    console.log(`🔄 Retry attempt ${this.state.retryCount + 1}`);
    this.setState({ 
      hasError: false, 
      errorDetails: null,
      errorInfo: null,
      retryCount: this.state.retryCount + 1 
    });
  };

  handleRefresh = () => {
    console.log('🔃 Full page refresh requested');
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { errorDetails, retryCount } = this.state;
      
      return (
        <div style={{
          padding: '30px',
          margin: '20px auto',
          maxWidth: '700px',
          backgroundColor: '#fff3cd',
          border: '2px solid #ffc107',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
            <span style={{ fontSize: '48px', marginRight: '15px' }}>⚠️</span>
            <h2 style={{ margin: 0, color: '#856404' }}>
              Agentic AI Encountered an Error
            </h2>
          </div>
          
          {/* Error Details */}
          <div style={{ marginBottom: '20px' }}>
            <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#856404' }}>
              What happened:
            </p>
            <pre style={{
              backgroundColor: '#f8f9fa',
              padding: '15px',
              borderRadius: '6px',
              border: '1px solid #dee2e6',
              overflow: 'auto',
              fontSize: '13px',
              lineHeight: '1.5',
              color: '#212529',
              maxHeight: '150px'
            }}>
              {errorDetails?.message || 'Unknown error occurred'}
              {errorDetails?.stack && retryCount > 1 && (
                '\n\nStack trace:\n' + errorDetails.stack
              )}
            </pre>
          </div>
          
          {/* User Actions */}
          <div style={{ marginBottom: '20px' }}>
            <p style={{ margin: '0 0 10px 0', fontWeight: 'bold', color: '#856404' }}>
              What you can do:
            </p>
            <ul style={{ 
              margin: '0', 
              paddingLeft: '20px',
              lineHeight: '1.8',
              color: '#856404'
            }}>
              <li>Click <strong>"Retry"</strong> to attempt the conversation again</li>
              <li>Try rephrasing your question or using simpler language</li>
              <li>Click <strong>"Refresh Page"</strong> to start with a clean slate</li>
              {retryCount >= 2 && (
                <li style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                  Multiple retries failed - consider refreshing the page or checking your internet connection
                </li>
              )}
            </ul>
          </div>
          
          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '10px',
            flexWrap: 'wrap',
            paddingTop: '10px',
            borderTop: '1px solid #ffc107'
          }}>
            <button 
              onClick={this.handleRetry}
              style={{
                padding: '12px 24px',
                backgroundColor: retryCount >= 2 ? '#6c757d' : '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '500',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
            >
              <span style={{ fontSize: '18px' }}>🔄</span>
              Retry {retryCount > 0 && `(Attempt ${retryCount + 1})`}
            </button>
            
            <button 
              onClick={this.handleRefresh}
              style={{
                padding: '12px 24px',
                backgroundColor: retryCount >= 2 ? '#dc3545' : '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: '500',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
              }}
            >
              <span style={{ fontSize: '18px' }}>🔃</span>
              Refresh Page
            </button>
          </div>
          
          {/* Retry Counter Badge */}
          {retryCount > 0 && (
            <div style={{
              marginTop: '15px',
              padding: '8px 12px',
              backgroundColor: retryCount >= 2 ? '#f8d7da' : '#d1ecf1',
              border: `1px solid ${retryCount >= 2 ? '#f5c6cb' : '#bee5eb'}`,
              borderRadius: '4px',
              fontSize: '13px',
              color: retryCount >= 2 ? '#721c24' : '#0c5460'
            }}>
              <strong>Retry count:</strong> {retryCount} 
              {retryCount >= 2 && ' ⚠️ Consider refreshing the page'}
            </div>
          )}
        </div>
      );
    }

    // No error - render children normally
    return this.props.children;
  }
}

export default AgentErrorBoundary;
